/**
 * MenuSandbox — práctica del menú nuevo sin tocar el menú real.
 *
 * Lo importante que enseña: **«Por secciones»** (el estilo nuevo, sólo títulos y
 * el contenido dentro) frente a **«Clásico»** (todas las entradas con
 * acordeones). Reutiliza `MENU_SECTIONS` y `DOMAIN_GROUPS` de `menuConfig.js`,
 * así que si cambia el menú, cambia la sandbox. Todo el estado es local.
 */

import React, { useState } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  StarIcon,
  EyeSlashIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { DOMAIN_GROUPS, MENU_SECTIONS, findMenuItem } from '../../menuConfig';

const labelOf = (key) => findMenuItem(key)?.label || key;

function itemLabel(key) {
  return labelOf(key);
}

export default function MenuSandbox() {
  const [style, setStyle] = useState('sections'); // 'sections' | 'classic'
  const [openSection, setOpenSection] = useState(null);
  const [openGroups, setOpenGroups] = useState(() => new Set(['physical']));
  const [hidden, setHidden] = useState(() => new Set());
  const [favorites, setFavorites] = useState(['racks']);
  const [page, setPage] = useState({ key: 'dashboard', label: 'Dashboard' });
  const [panel, setPanel] = useState(false);
  const [query, setQuery] = useState('');

  const normalized = query.trim().toLowerCase();

  const visible = (key) => !hidden.has(key) && (!normalized || itemLabel(key).toLowerCase().includes(normalized));

  const favoriteRows = favorites
    .filter((key) => !hidden.has(key))
    .map((key) => ({ key, label: itemLabel(key) }));

  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleFavorite = (key, label) => {
    setFavorites((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]));
    if (!page.key) setPage({ key, label });
  };

  const openPage = (key) => setPage({ key, label: itemLabel(key) });

  const hide = (key, shouldHide) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (shouldHide) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  /**
   * Entradas de una sección ya resueltas: enlaces sueltos y grupos.
   *
   * Los grupos traen dentro las entradas que la sección ya pinta sueltas
   * (`skipItems` en `menuConfig`, más los enlaces de la propia sección): se
   * filtran aquí para que no salgan duplicadas, igual que en el menú real.
   */
  const entriesOf = (section) => {
    const linkKeys = section.entries.filter((entry) => entry.type === 'link').map((entry) => entry.key);
    return section.entries
      .map((entry) => {
        if (entry.type === 'link') {
          const item = findMenuItem(entry.key);
          return item ? { type: 'link', item } : null;
        }
        const group = DOMAIN_GROUPS.find((candidate) => candidate.key === entry.key);
        if (!group) return null;
        const skip = new Set([...(entry.skipItems || []), ...linkKeys]);
        return {
          type: 'group',
          group: { ...group, items: (group.items || []).filter((item) => !skip.has(item.key)) },
        };
      })
      .filter(Boolean);
  };

  const renderGroup = (group) => {
    const items = (group.items || []).filter((item) => visible(item.key));
    if (!items.length) return null;
    const isOpen = openGroups.has(group.key) || normalized;
    return (
      <div key={group.key} className="acs-group">
        <button type="button" className="acs-group-head" onClick={() => toggleGroup(group.key)} aria-expanded={isOpen}>
          {isOpen ? <ChevronDownIcon className="acs-chevron" /> : <ChevronRightIcon className="acs-chevron" />}
          <span className="acs-group-dot" style={{ background: group.color }} aria-hidden="true" />
          <span className="acs-group-label">{group.label}</span>
        </button>
        {isOpen && (
          <ul className="acs-items">
            {items.map((item) => (
              <li key={item.key} className="acs-item">
                <button type="button" className="acs-item-btn" onClick={() => openPage(item.key)}>
                  {item.label}
                </button>
                <button
                  type="button"
                  className={`acs-icon-btn${favorites.includes(item.key) ? ' is-on' : ''}`}
                  onClick={() => toggleFavorite(item.key, item.label)}
                  title={favorites.includes(item.key) ? 'Quitar de favoritos' : 'Añadir a favoritos'}
                  aria-label={favorites.includes(item.key) ? 'Quitar de favoritos' : 'Añadir a favoritos'}
                >
                  <StarIcon className="acs-icon" />
                </button>
                <button
                  type="button"
                  className="acs-icon-btn"
                  onClick={() => hide(item.key, true)}
                  title="Ocultar del menú"
                  aria-label="Ocultar del menú"
                >
                  <EyeSlashIcon className="acs-icon" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const renderLink = (item) =>
    visible(item.key) ? (
      <button key={item.key} type="button" className="acs-link" onClick={() => openPage(item.key)}>
        {item.label}
      </button>
    ) : null;

  const sections = MENU_SECTIONS.filter((section) => section.label);

  const currentSection = sections.find((section) => section.key === openSection);
  const currentEntries = currentSection ? entriesOf(currentSection) : [];

  const favoriteKey = page.key || 'dashboard';

  return (
    <div className="acs2-frame">
      <div className="acs2-toolbar">
        <div className="acs2-styles" role="group" aria-label="Estilo del menú">
          <button
            type="button"
            className={`acs2-style${style === 'sections' ? ' is-active' : ''}`}
            onClick={() => { setStyle('sections'); setOpenSection(null); }}
          >
            Por secciones
          </button>
          <button
            type="button"
            className={`acs2-style${style === 'classic' ? ' is-active' : ''}`}
            onClick={() => setStyle('classic')}
          >
            Clásico
          </button>
        </div>
        <div className="acs2-search">
          <MagnifyingGlassIcon className="acs2-search-icon" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar entrada"
            aria-label="Buscar entrada en el menú de la sandbox"
          />
        </div>
        <button type="button" className="acs2-customize" onClick={() => setPanel(true)}>
          Personalizar menú
        </button>
      </div>

      <div className="acs2-body">
        <div className="acs2-sidebar">
          <div className="acs2-brand">L6 <span>CMDB</span></div>
          <div className="acs2-company">
            <span className="acs2-company-label">EMPRESA</span>
            <span className="acs2-company-name">L6 Ecosystem</span>
          </div>

          {favoriteRows.length > 0 && (
            <div className="acs2-favs">
              <span className="acs2-section-label">FAVORITOS</span>
              {favoriteRows.map((row) => (
                <div key={row.key} className="acs2-fav">
                  <StarIcon className="acs-icon" />
                  <span>{row.label}</span>
                  <button
                    type="button"
                    className="acs-icon-btn"
                    onClick={() => toggleFavorite(row.key, row.label)}
                    aria-label="Quitar de favoritos"
                    title="Quitar de favoritos"
                  >
                    <XMarkIcon className="acs-icon" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {style === 'sections' ? (
            currentSection ? (
              <>
                <button type="button" className="acs2-back" onClick={() => setOpenSection(null)}>
                  <ChevronLeftIcon className="acs-chevron" /> Todas las secciones
                </button>
                <span className="acs2-section-label">{currentSection.label}</span>
                {currentEntries.map((entry) =>
                  entry.type === 'link' ? renderLink(entry.item) : renderGroup(entry.group)
                )}
              </>
            ) : (
              <>
                {MENU_SECTIONS.filter((section) => !section.label).map((section) =>
                  section.entries.map((entry) => {
                    const item = findMenuItem(entry.key);
                    return item && visible(item.key) ? (
                      <button key={entry.key} type="button" className="acs-link" onClick={() => openPage(item.key)}>
                        {item.label}
                      </button>
                    ) : null;
                  })
                )}
                {sections.map((section) => (
                  <button
                    key={section.key}
                    type="button"
                    className="acs2-section"
                    onClick={() => setOpenSection(section.key)}
                  >
                    <span className="acs2-section-name">{section.label}</span>
                    <ChevronRightIcon className="acs-chevron" />
                  </button>
                ))}
              </>
            )
          ) : (
            sections.map((section) => (
              <div key={section.key} className="acs2-classic-section">
                <span className="acs2-section-label">{section.label}</span>
                {entriesOf(section).map((entry) =>
                  entry.type === 'link' ? renderLink(entry.item) : renderGroup(entry.group)
                )}
              </div>
            ))
          )}

          <button type="button" className="acs2-add-fav" onClick={() => setPanel(true)}>
            <StarIcon className="acs-icon" /> {favorites.length ? 'Editar favoritos' : 'Añadir favoritos'}
          </button>
        </div>

        <div className="acs2-main">
          <div className="acs2-topbar">
            <span className="acs2-topbar-page">{page.label}</span>
            <button
              type="button"
              className={`acs2-star${favorites.includes(favoriteKey) ? ' is-on' : ''}`}
              onClick={() => toggleFavorite(favoriteKey, page.label)}
              title={favorites.includes(favoriteKey) ? 'Quitar de favoritos' : `Añadir «${page.label}» a favoritos`}
            >
              <StarIcon className="acs-icon" />
            </button>
          </div>
          <div className="acs2-content">
            <p className="acs2-content-title">{page.label}</p>
            <p className="acs2-content-hint">
              {style === 'sections'
                ? 'Estilo nuevo: el menú sólo enseña los títulos; al entrar en una sección aparece «‹ Todas las secciones».'
                : 'Estilo clásico: todas las entradas a la vista, agrupadas en acordeones.'}
            </p>
            <div className="acs2-counters">
              <span><strong>{favorites.length}</strong> favoritos</span>
              <span><strong>{hidden.size}</strong> ocultas</span>
            </div>
          </div>
        </div>
      </div>

      {panel && (
        <div className="acs2-panel" role="dialog" aria-label="Personalizar menú (sandbox)">
          <div className="acs2-panel-head">
            <span>Personalizar menú</span>
            <button type="button" className="acs-icon-btn" onClick={() => setPanel(false)} aria-label="Cerrar">
              <XMarkIcon className="acs-icon" />
            </button>
          </div>
          <p className="acs2-panel-hint">Desmarca lo que no quieras ver. En la app esto se guarda en tu preferencia.</p>
          <div className="acs2-panel-list">
            {MENU_SECTIONS.flatMap((section) =>
              entriesOf(section).flatMap((entry) =>
                entry.type === 'link' ? [entry.item] : entry.group.items
              )
            ).map((item) => (
              <label key={item.key} className="acs2-check">
                <input
                  type="checkbox"
                  checked={!hidden.has(item.key)}
                  onChange={(event) => hide(item.key, !event.target.checked)}
                />
                {item.label}
              </label>
            ))}
          </div>
          <div className="acs2-panel-foot">
            <button type="button" className="acs2-reset" onClick={() => { setHidden(new Set()); setFavorites(['racks']); setQuery(''); }}>
              Mostrar todo y reiniciar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
